import React, { useCallback } from 'react';
import { LTText, LTTooltip } from '@lambdatestincprivate/lt-components';
import {
  getStatusBackgroundColor,
  formatDateForTooltip,
  capitalizeFirst
} from './helpers';
import styles from './AIRca.module.css';

const ErrorRepetitionTimeline = ({ errorRepetitionData, category, baseUrl = '' }) => {
  const handleDotClick = useCallback((testId) => {
    if (!testId) return;
    const base = baseUrl.replace(/\/$/, '');
    window.open(`${base}/test?testID=${testId}`, '_blank', 'noopener,noreferrer');
  }, [baseUrl]);

  if (!errorRepetitionData || !Array.isArray(errorRepetitionData)) {
    return null;
  }

  const similarErrorCount = errorRepetitionData.filter(
    (val) => val?.rca_category === category
  ).length;
  const errorCount = errorRepetitionData.filter(
    (val) => !['passed', 'completed'].includes(val?.status?.toLowerCase?.())
  ).length;
  const total = errorRepetitionData.length;
  const initialDots = Math.max(0, 10 - total);

  const tooltipText =
    'Show the status of the test in last 10 runs. Highlights where the test failed due to the same reasons.';

  return (
    <div className={styles.errorTrendsContainer}>
      <div className={styles.errorTrendsHeader}>
        <div className={styles.errorTrendsHeaderContent}>
          <LTTooltip text={tooltipText}>
            <LTText
              color="text.muted"
              variant={LTText.TextVariant.SMALL_REGULAR}
              text="Error Trends (Test)"
            />
          </LTTooltip>
          <span>
            <LTText
              text={`${similarErrorCount} `}
              variant={LTText.TextVariant.SMALL_BOLD}
            />
            <LTText
              text={`/ ${errorCount}`}
              variant={LTText.TextVariant.SMALL_REGULAR}
            />
          </span>
        </div>
      </div>
      <div className={styles.trackWrapper}>
        <div className={styles.trackInner}>
          <div className={styles.trackLine} />
          <div className={styles.dotsContainer}>
            {initialDots > 0 &&
              Array.from({ length: initialDots }).map((_, i) => (
                <div key={`initial-${i}`} className={styles.placeholderDot} />
              ))}
            {errorRepetitionData.map((occurrence, i) => {
              const isSameCategory = category === occurrence?.rca_category;
              const tip = `Date: ${formatDateForTooltip(
                occurrence?.create_timestamp
              )}\nTest ID: ${occurrence?.test_id ||
                ''}\nStatus: ${capitalizeFirst(occurrence?.status || '')}${
                occurrence?.rca_category
                  ? `\nRCA Category: ${occurrence.rca_category}`
                  : ''
              }`;

              return (
                <div
                  key={occurrence?.test_id || `dot-${i}`}
                  className={styles.dotWrapper}
                >
                  <LTTooltip text={tip}>
                    {isSameCategory ? (
                      <div
                        tabIndex={0}
                        role="button"
                        className={styles.sameCategoryDot}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDotClick(occurrence?.test_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ')
                            handleDotClick(occurrence?.test_id);
                        }}
                      >
                        <div className={styles.sameCategoryDotInner} />
                      </div>
                    ) : (
                      <div
                        tabIndex={0}
                        role="button"
                        className={styles.statusDot}
                        style={{
                          backgroundColor: getStatusBackgroundColor(
                            occurrence?.status
                          ),
                          cursor: 'pointer'
                        }}
                        onClick={() => handleDotClick(occurrence?.test_id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ')
                            handleDotClick(occurrence?.test_id);
                        }}
                      />
                    )}
                  </LTTooltip>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorRepetitionTimeline;
